import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, MAIN_SITE_URL } from '@/data/categories';
import JsonLd from '@/components/feature/JsonLd';
import { SITE_URL, BRAND_NAME, FAVICON_URL, OG_IMAGE } from '@/lib/seo';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'USFans Spreadsheet: The Complete Guide',
  description:
    'Learn what the USFans spreadsheet is, what you can find inside it, how to use it, and get practical tips — plus a comparison table and FAQ.',
  image: OG_IMAGE,
  author: { '@type': 'Organization', name: BRAND_NAME },
  publisher: {
    '@type': 'Organization',
    name: BRAND_NAME,
    logo: { '@type': 'ImageObject', url: FAVICON_URL },
  },
  datePublished: '2026-08-15',
  dateModified: '2026-08-21',
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/usfans-spreadsheet-guide` },
};

function FaqBlock() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<number | null>(0);

  const items = [1, 2, 3, 4, 5].map((i) => ({
    q: t(`article.faq.q${i}`),
    a: t(`article.faq.a${i}`),
  }));

  return (
    <div className="divide-y divide-background-200/70 rounded-lg border border-background-200/70 bg-background-50">
      {items.map((item, idx) => {
        const isOpen = open === idx;
        return (
          <div key={idx}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : idx)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-background-100/60"
            >
              <span className="text-sm font-semibold text-foreground-900 md:text-base">{item.q}</span>
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-transform ${
                  isOpen ? 'rotate-180 bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i className="ri-arrow-down-s-line text-sm" />
              </span>
            </button>
            {isOpen && (
              <div className="px-5 pb-5">
                <p className="text-sm leading-relaxed text-foreground-600 md:text-base">{item.a}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ComparisonTable() {
  const { t } = useTranslation();

  const rows = [1, 2, 3, 4, 5].map((i) => ({
    factor: t(`article.secCompare.r${i}factor`),
    v1: t(`article.secCompare.r${i}v1`),
    v2: t(`article.secCompare.r${i}v2`),
    best: t(`article.secCompare.r${i}best`),
  }));

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200/70">
      <table className="w-full min-w-[640px] border-collapse bg-background-50 text-sm">
        <thead>
          <tr className="bg-background-100/70 text-left">
            <th className="px-4 py-3 font-semibold text-foreground-900">Factor</th>
            <th className="px-4 py-3 font-semibold text-foreground-900">{t('article.secCompare.col1')}</th>
            <th className="px-4 py-3 font-semibold text-foreground-900">{t('article.secCompare.col2')}</th>
            <th className="px-4 py-3 font-semibold text-foreground-900">{t('article.secCompare.col3')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-background-200/60">
          {rows.map((r, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/40'}>
              <td className="px-4 py-3 font-semibold text-foreground-900">{r.factor}</td>
              <td className="px-4 py-3 text-foreground-700">{r.v1}</td>
              <td className="px-4 py-3 text-foreground-700">{r.v2}</td>
              <td className="px-4 py-3 text-foreground-700">{r.best}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ArticlePage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('article.title');
  }, [t]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd} />
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="bg-background-100/60 border-b border-background-200/70">
          <div className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              <i className="ri-arrow-left-line" />
              {t('article.backHome')}
            </Link>
            <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {t('article.title')}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-line" />
                {t('article.author')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {t('article.updated')}
              </span>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          {/* Quick Answer */}
          <div className="rounded-lg border-l-4 border-accent-500 bg-accent-50 p-5 md:p-6">
            <p className="text-sm font-bold uppercase tracking-wide text-accent-900">{t('article.quickAnswerLabel')}</p>
            <p className="mt-2 text-base leading-relaxed text-foreground-700">{t('article.quickAnswer')}</p>
          </div>

          <p className="mt-8 text-base leading-relaxed text-foreground-700">{t('article.intro')}</p>

          {/* Section: What Is */}
          <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t('article.secWhat.title')}</h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.secWhat.p1')}</p>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.secWhat.p2')}</p>

          {/* Section: What You Can Find */}
          <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t('article.secFind.title')}</h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.secFind.p1')}</p>
          <ul className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {categories.map((c) => (
              <li key={c.id}>
                <a
                  href={c.href}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 rounded-md border border-background-200/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-primary-100 text-primary-700">
                    <i className="ri-check-line text-xs" />
                  </span>
                  {t(`cat.${c.id}`)}
                </a>
              </li>
            ))}
          </ul>

          {/* Section: How to Use */}
          <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t('article.secHow.title')}</h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.secHow.p1')}</p>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.secHow.p2')}</p>

          {/* Section: Popular Categories */}
          <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t('article.secPopular.title')}</h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.secPopular.p1')}</p>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.secPopular.p2')}</p>

          {/* Section: Comparison Table */}
          <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t('article.secCompare.title')}</h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t('article.secCompare.p1')}</p>
          <div className="mt-5">
            <ComparisonTable />
          </div>

          {/* Section: Tips */}
          <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t('article.secTips.title')}</h2>
          <ul className="mt-5 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <li key={i} className="flex gap-3 rounded-lg border border-background-200/70 bg-background-50 p-4">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-bold">
                  {i}
                </span>
                <p className="text-sm leading-relaxed text-foreground-700">{t(`article.secTips.tip${i}`)}</p>
              </li>
            ))}
          </ul>

          {/* FAQ */}
          <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{t('article.faq.title')}</h2>
          <div className="mt-5">
            <FaqBlock />
          </div>

          {/* Internal links + CTA */}
          <div className="mt-12 rounded-lg border border-background-200/70 bg-background-100/60 p-6 text-center md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{t('article.related.title')}</h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-foreground-600">{t('article.related.text')}</p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/"
                className="inline-flex w-full items-center justify-center rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap sm:w-auto"
              >
                {t('article.backHome')}
              </Link>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center rounded-md border border-primary-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 whitespace-nowrap sm:w-auto"
              >
                {t('article.ctaButton')}
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}